import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import { Range, getTrackBackground } from "react-range";
import Modal from "../modal/Modal"; // Import the Modal component
import CustomInput from "../custom_components/CustomInput"; // Import the CustomInput component
import apiService from "../../api/apiService";
import { getCookie } from "../../utils/cookieUtils"; // Import getCookie
import {
  PlayFill,
  PauseFill,
  ArrowShapeDownToLine,
  MusicNote,
  Xmark,
  Ellipsis,
  VolumeLow,
  VolumeOff,
  VolumeXmark,
  Volume,
  ChevronDown,
} from "@gravity-ui/icons";
import {
  FaPlay,
  FaPause,
  FaStepBackward,
  FaStepForward,
  FaVolumeUp,
  FaVolumeMute,
  FaEllipsisH,
} from "react-icons/fa";
import { TbRewindBackward10, TbRewindForward10 } from "react-icons/tb";
import Toast from "../generic_components/Toast";
import { useLocation } from "react-router-dom";
import AudioProcessor from "../custom_components/AudioProcessor";
import { usePostHog } from "posthog-js/react";
import { useLocalState } from "../../contexts/LocalStateProvider";
import ProgressInfoToast from "../custom_components/ProgressInfoToast";
import DownloadModal from "./DownloadModal"; // Import DownloadModal
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";

const AudioPlayer = () => {
  const {
    audioSrc,
    title,
    description,
    isPlaying,
    pauseAudio,
    resumeAudio,
    stopAudio,
    currentTime,
    duration,
    volume,
    seekAudio,
    changeVolume,
    imageSrc,
    isBuffering,
    streamingSrc,
    aiResponseId,
    discoverId,
  } = useAudioPlayer();
  const posthog = usePostHog();
  //console.log('[AudioPlayer] Component Render. audioSrc:', audioSrc, 'currentTime:', currentTime, 'duration:', duration, 'isPlaying:', isPlaying, 'isBuffering:', isBuffering);
  const [showVolumeSlider, setShowVolumeSlider] = useState(true);
  const [showVolume, setShowVolume] = useState(true);
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");
  const [isVolumeHovered, setIsVolumeHovered] = useState(false);
  const [isPlaybackHovered, setIsPlaybackHovered] = useState(false); // State to track hover on playback progress bar
  const [isPlaybackDragging, setIsPlaybackDragging] = useState(false);
  const [isVolumeDragging, setIsVolumeDragging] = useState(false);
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const { localState, updateLocalState } = useLocalState();
  const selectedTrackDetails = localState.selectedTrackDetails; // Read selection details
  //console.log("selected track details and local state from audio player", selectedTrackDetails, localState);

  // Show the spinner only if the current audio is the one streaming
  const isCurrentAudioBuffering = isBuffering && streamingSrc === audioSrc;
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGeneratingStems, setIsGeneratingStems] = useState(false);
  const [stemProgress, setStemProgress] = useState(0);
  const [stemTrackName, setStemTrackName] = useState(""); // Add state for track name

  // --- EDIT 1: Manage badge display with useState and useEffect, and add latching ---
  const [displayBadge, setDisplayBadge] = useState(false);
  const [badgeTrackIndex, setBadgeTrackIndex] = useState(null);

  // State for latching the playingAIChatBoxTrackIndex
  const [latchedPlayingIndex, setLatchedPlayingIndex] = useState(null);
  const [latchedPlayingIndexAiResponseId, setLatchedPlayingIndexAiResponseId] = useState(null);

  // --- EDIT 1: Add state for track index to be used for download ---
  const [trackIndexForDownload, setTrackIndexForDownload] = useState(0);

  // Add state for repeat and autoplay buttons
  const [isRepeatEnabled, setIsRepeatEnabled] = useState(false);
  const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(false);
  
  // Track user manual interactions to distinguish from autoplay
  const [userManuallyPlayed, setUserManuallyPlayed] = useState(false);

  // --- EDIT 1: Add state for tooltip management ---
  const [activeTooltip, setActiveTooltip] = useState(null);

  useEffect(() => {
    let shouldDisplay = false;
    let trackIdx = null;

    if (aiResponseId) { // Ensure it's an AI-generated track
      // Priority 1: Use playingAIChatBoxTrackIndex from currentSongDetails
      if (
        localState.currentSongDetails &&
        localState.currentSongDetails.ai_response_id === aiResponseId &&
        localState.currentSongDetails.playingAIChatBoxTrackIndex !== undefined &&
        localState.currentSongDetails.playingAIChatBoxTrackIndex !== null
      ) {
        shouldDisplay = true;
        trackIdx = localState.currentSongDetails.playingAIChatBoxTrackIndex;
        // Update latch if P1 is met
        if (latchedPlayingIndexAiResponseId !== aiResponseId || latchedPlayingIndex !== trackIdx) {
          setLatchedPlayingIndex(trackIdx);
          setLatchedPlayingIndexAiResponseId(aiResponseId);
        }
      }
      // Priority 1.5: Use latched playingAIChatBoxTrackIndex if P1 failed but aiResponseId matches latch
      else if (
        latchedPlayingIndexAiResponseId === aiResponseId &&
        latchedPlayingIndex !== null
      ) {
        shouldDisplay = true;
        trackIdx = latchedPlayingIndex;
      }
      // Priority 2: Fallback to selectedTrackDetails (user checkbox selection)
      else if (
        localState.selectedTrackDetails &&
        localState.selectedTrackDetails.responseId === aiResponseId &&
        localState.selectedTrackDetails.trackIndex !== undefined &&
        localState.selectedTrackDetails.trackIndex !== null
      ) {
        shouldDisplay = true;
        trackIdx = localState.selectedTrackDetails.trackIndex;
        // Clear P1 latch if P2 (explicit selection) is met
        if (latchedPlayingIndexAiResponseId !== null) {
          setLatchedPlayingIndex(null);
          setLatchedPlayingIndexAiResponseId(null);
        }
      }
      // Priority 3: Fallback for preview-initiated plays
      else if (
        localState.currentSongDetails &&
        localState.currentSongDetails.ai_response_id === aiResponseId &&
        localState.currentSongDetails.selectedTrackIndexForPreview !== undefined &&
        localState.currentSongDetails.selectedTrackIndexForPreview !== null
      ) {
        shouldDisplay = true;
        trackIdx = localState.currentSongDetails.selectedTrackIndexForPreview;
        // Clear P1 latch if P3 (explicit preview) is met
        if (latchedPlayingIndexAiResponseId !== null) {
          setLatchedPlayingIndex(null);
          setLatchedPlayingIndexAiResponseId(null);
        }
      }
    }

    // If aiResponseId has changed and no valid index was found by any priority, clear the latch for the old aiResponseId.
    if (aiResponseId !== latchedPlayingIndexAiResponseId && !shouldDisplay) {
        if (latchedPlayingIndexAiResponseId !== null) { // Only clear if there was a previous latch
            setLatchedPlayingIndex(null);
            setLatchedPlayingIndexAiResponseId(null);
        }
    }

    // If it's a discover track, never show the version badge.
    if (discoverId) {
      shouldDisplay = false;
      trackIdx = null; // Ensure badgeTrackIndex is also cleared
    }

    setDisplayBadge(shouldDisplay);
    setBadgeTrackIndex(trackIdx);

  }, [
    aiResponseId,
    discoverId, // Add discoverId to dependencies
    localState.currentSongDetails,
    localState.selectedTrackDetails,
    latchedPlayingIndex,
    latchedPlayingIndexAiResponseId
  ]);
  // --- END EDIT 1 ---

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Effect to sync autoplay state with AIChatBox autoplay
  useEffect(() => {
    // Check if AIChatBox is currently in autoplay mode by looking at the isAutoplaying field
    const isCurrentlyAutoplaying = localState.currentSongDetails?.isAutoplaying === true &&
                                   isPlaying &&
                                   aiResponseId &&
                                   !isRepeatEnabled;
    
    // Be more conservative about when to update autoplay state
    // Only sync FROM AIChatBox TO AudioPlayer, not the other way around
    // And only if the user hasn't explicitly set their own preference
    if (isCurrentlyAutoplaying && !isAutoplayEnabled && !userManuallyPlayed) {
      console.log('[AudioPlayer] AIChatBox autoplay detected, enabling button');
      setIsAutoplayEnabled(true);
    }
    
    // If autoplay stops in AIChatBox and we had it enabled, keep it enabled
    // (user preference should persist until they manually toggle it)
    
    // If genuine autoplay becomes active and repeat is enabled, turn off repeat (mutual exclusivity)
    if (isCurrentlyAutoplaying && isRepeatEnabled && !userManuallyPlayed) {
      console.log('[AudioPlayer] Genuine autoplay detected, disabling repeat');
      setIsRepeatEnabled(false);
    }
  }, [localState.currentSongDetails, isPlaying, isAutoplayEnabled, isRepeatEnabled, aiResponseId, userManuallyPlayed]);

  // Reference to track previous playing state for repeat functionality
  const wasPlayingForRepeatRef = useRef(false);
  
  // Effect to handle user-initiated autoplay from AudioPlayer button
  useEffect(() => {
    if (localState.triggerAutoplayFromAudioPlayer?.enabled && isAutoplayEnabled) {
      console.log('[AudioPlayer] User enabled autoplay - looking for tracks to play');
      
      // Find the latest AI response to start autoplay
      // This should trigger the AIChatBox autoplay system
      updateLocalState("userRequestedAutoplay", {
        enabled: true,
        timestamp: Date.now()
      });
      
      // Clear the trigger after processing
      setTimeout(() => {
        updateLocalState("triggerAutoplayFromAudioPlayer", null);
      }, 1000);
    }
  }, [localState.triggerAutoplayFromAudioPlayer, isAutoplayEnabled, updateLocalState]);

  // Effect to maintain autoplay state when new tracks are generated
  useEffect(() => {
    // If user has autoplay enabled and a new AI response starts playing automatically,
    // don't let the sync effect turn off the autoplay button
    if (isAutoplayEnabled && aiResponseId && isPlaying) {
      const isCurrentlyAutoplaying = localState.currentSongDetails?.isAutoplaying === true;
      
      if (isCurrentlyAutoplaying) {
        console.log('[AudioPlayer] Maintaining autoplay state for new generation');
        // Reset the manual play flag since this is autoplay
        setUserManuallyPlayed(false);
      }
    }
  }, [aiResponseId, isPlaying, isAutoplayEnabled, localState.currentSongDetails]);

  // Effect to handle repeat functionality
  useEffect(() => {
    if (isRepeatEnabled && audioSrc && duration && duration > 0) {
      // If the track was playing and now stopped, and we're near the end, it likely finished
      if (wasPlayingForRepeatRef.current && !isPlaying && currentTime >= duration - 1 && currentTime < duration + 1) {
        // Track ended naturally, restart it
        console.log('[AudioPlayer] Repeat triggered - restarting track');
        setTimeout(() => {
          setUserManuallyPlayed(true); // Mark as manual interaction to prevent autoplay interference
          seekAudio(0);
          resumeAudio();
          posthog.capture("Audio-Repeat-Triggered", {
            is_user_logged_in: true,
            trackName: title
          });
        }, 200);
      }
    }
    
    wasPlayingForRepeatRef.current = isPlaying;
  }, [isRepeatEnabled, isPlaying, currentTime, duration, audioSrc, seekAudio, resumeAudio, title, posthog]);

  // Handlers for the new buttons
  const handleRepeatToggle = () => {
    const newRepeatState = !isRepeatEnabled;
    setIsRepeatEnabled(newRepeatState);
    
    // If enabling repeat, disable autoplay (mutual exclusivity)
    if (newRepeatState && isAutoplayEnabled) {
      setIsAutoplayEnabled(false);
      
      // Also signal to AIChatBox to stop any active autoplay
      updateLocalState("userAutoplayPreference", false);
      updateLocalState("triggerAutoplayFromAudioPlayer", null);
      console.log('[AudioPlayer] Repeat enabled - disabling autoplay and signaling to AIChatBox');
    } else if (!newRepeatState) {
      // If disabling repeat, clear the autoplay preference flag to avoid interference
      updateLocalState("userAutoplayPreference", null);
      console.log('[AudioPlayer] Repeat disabled - clearing autoplay preference flag');
    }

    posthog.capture("Audio-Repeat-Toggle", {
      is_user_logged_in: true,
      enabled: newRepeatState,
      trackName: title
    });
  };

  const handleAutoplayToggle = () => {
    const newAutoplayState = !isAutoplayEnabled;
    
    // If enabling autoplay, disable repeat (mutual exclusivity)
    if (newAutoplayState && isRepeatEnabled) {
      setIsRepeatEnabled(false);
    }

    setIsAutoplayEnabled(newAutoplayState);
    
    // Store the user's autoplay preference in localState for AIChatBox to potentially use
    updateLocalState("userAutoplayPreference", newAutoplayState);
    
    // If enabling autoplay, trigger autoplay behavior
    if (newAutoplayState) {
      // Signal to AIChatBox components that user wants autoplay
      updateLocalState("triggerAutoplayFromAudioPlayer", {
        enabled: true,
        timestamp: Date.now()
      });
      console.log('[AudioPlayer] Autoplay enabled - signaling to AIChatBox');
    } else {
      // If disabling autoplay, stop current playback if it's autoplaying
      if (isPlaying && aiResponseId) {
        pauseAudio();
        console.log('[AudioPlayer] Autoplay disabled - pausing current track');
      }
      updateLocalState("triggerAutoplayFromAudioPlayer", null);
      updateLocalState("userAutoplayPreference", null); // Clear the preference flag
    }
    
    posthog.capture("Audio-Autoplay-Toggle", {
      is_user_logged_in: true,
      enabled: newAutoplayState,
      trackName: title,
      hasAiResponseId: !!aiResponseId
    });

    console.log('[AudioPlayer] Autoplay toggled to:', newAutoplayState);
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      pauseAudio();
      setUserManuallyPlayed(false); // Reset when pausing
    } else {
      //console.log('[AudioPlayer] handlePlayPause: Calling resumeAudio. Current time:', currentTime, 'Duration:', duration);
      setUserManuallyPlayed(true); // Mark as manual play
      resumeAudio();
    }
  };

  const handleSeek = (values) => {
    if (isBuffering) {
      return;
    }

    if (duration && isFinite(duration) && duration > 0) {
      const newTime = (values[0] / 100) * duration;
      const oldTime = currentTime;
      seekAudio(newTime);
      
      // Only capture the event if it's an actual user interaction (when dragging ends)
      if (isPlaybackDragging) {
        posthog.capture("Audio-ProgressBar-Drag", {
          is_user_logged_in: true,
          trackName: title,
          fromPosition: Math.round(oldTime),
          toPosition: Math.round(newTime),
          percentComplete: Math.round(values[0]),
        });
      }
    }
  };

  const handleVolumeChange = (values) => {
    const oldVolume = volume * 100;
    changeVolume(values[0] / 100);
    
    // Only capture the event if it's an actual user interaction (when dragging)
    if (isVolumeDragging) {
      posthog.capture("Audio-VolumeBar-Change", {
        trackName: title,
        fromVolume: Math.round(oldVolume),
        toVolume: Math.round(values[0]),
        is_user_logged_in: true
      });
    }
  };

  const mute = () => {
    changeVolume(showVolume ? 0 : 1);
    setShowVolume((prev) => !prev);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const handleOpenModal = async () => {
    if (!discoverId) {
      setToastMessage("Please select a song first.");
      return;
    }

    setIsLoadingAudio(true);
    setIsModalOpen(true);

    const taskName = "fetchDiscoverAudioFile";
    const requestPayload = { discoverId };

    try {
      const response = await apiService.sendRequest(taskName, requestPayload);
      const { audioFile } = response;

      if (audioFile.data) {
        const blob = new Blob([new Uint8Array(audioFile.data)], {
          type: "audio/wav",
        });

        const file = new File([blob], `${title}.wav`, { type: blob.type });

        setAudioFile(file);
        setIsLoadingAudio(false);
      } else {
      }
    } catch (error) {
      console.error("Error fetching the audio file:", error);
      Sentry.captureException(error);
    }
  };
  // --- EDIT 1: Determine if download should be disabled based on selection ---
  const isAiTrack = !!(aiResponseId && !discoverId);
  const isSelectionMissingForAiTrack = isAiTrack && selectedTrackDetails === null;
  const isDownloadDisabled = isBuffering ;
  // // --- END EDIT 1 ---

  // // --- EDIT 1: Combine disable conditions for both Download and Get Stems ---
  // new condition to check if the current track is the latest version
  const versionHistory = localState?.versionHistory?.[0];
  const isActionEnabled = (
    (
      localState?.selectedTrackDetails !== null &&
      localState?.selectedTrackDetails?.responseId === localState?.ai_response_id
    ) ||
    (
      localState?.currentSongDetails?.ai_response_id !== localState?.ai_response_id &&
      versionHistory?.versions?.length > 1
    )
    ) && !isBuffering




  // Disable only if buffering or (this is the latest track && no selection)
  const isActionDisabled = isBuffering // not being used for now
  //console.log("isActionDisabled",isActionDisabled,"isActionEnabled" ,isActionEnabled,localState)



  // --- END EDIT 1 ---

  const handleDownload = () => {
    // --- EDIT 2: Use the combined 'isActionDisabled' and specific message ---
    
    if (isBuffering) {
      setToastMessage("Please Allow Entire Audio to Load Before Downloading.")
      setToastType("error");
      return;
    }
    // if (isSelectionMissingForAiTrack) {
    //   setToastMessage("Please select a track version from the chat message before downloading.");
    //   setToastType("info");
    //   return;
    // }

    // --- END EDIT 2 ---
    
    posthog.capture("Click-Download-AudioPlayer", {
      trackName: title,
      is_user_logged_in: true
    });
    setToastMessage(null);
    setIsDownloadModalOpen(true);
  };

  const handleCustomize = (e) => {
    e.stopPropagation();
    posthog.capture("Click to Customize Discover Audio", {
      song: title,
      is_user_logged_in: true
    });

    const fileName = audioSrc?.split("/")?.pop();

    const fileData = {
      name: title,
      type: "audio/wav",
      url: `gs://discover_songs/${fileName}`,
    };

    setSelectedSong({
      audioFile: fileData,
      image: localState.audioImgSrc || imageSrc,
      mood: description,
    });

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowOptionsDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleGetStems = async () => { // Make function async
    if(isBuffering){
      setToastMessage("Please Allow Entire Audio to Load Before Generating Stems.")
      setToastType("error");
      return;
    }
    // if (isSelectionMissingForAiTrack) {
    //   setToastMessage("Please select a track version from the chat message before generating stems.");
    //   setToastType("info");
    //   return;
    // }
    // --- END EDIT 3 ---

    posthog.capture("Click-GetStems-AudioPlayer", {
      trackName: title,
      is_user_logged_in: true
    });
    setShowOptionsDropdown(false); // Close dropdown
    
    if (!aiResponseId) {
      setToastMessage("Cannot generate stems. Audio information missing.");
      setToastType("error");
      return;
    }

    // --- Determine track index for stems ---
    let trackIdxForStems = 0; // Default
    if (displayBadge && badgeTrackIndex !== null) {
        trackIdxForStems = badgeTrackIndex;
    } else if (audioSrc && aiResponseId && !discoverId) { // If playing an AI response track
        const srcParts = audioSrc.split('/');
        const potentialTrackIdStr = srcParts[srcParts.length - 1];
        const parsedIndex = parseInt(potentialTrackIdStr, 10);
        // Check if the part before it looks like a response ID
        const potentialResponseIdPart = srcParts[srcParts.length - 2];
        if (!isNaN(parsedIndex) && (potentialResponseIdPart?.length > 10 || potentialResponseIdPart?.includes('-'))) {
            trackIdxForStems = parsedIndex;
        } else {
            console.warn(`[AudioPlayer handleGetStems] Could not reliably parse track index from audioSrc: ${audioSrc} for aiResponseId: ${aiResponseId}. Defaulting to 0.`);
        }
    } else if (discoverId) {
      // For discoverId, the GCS URI endpoint might not need a track index, or it's implicitly 0.
      // The backend /api/discover-gcs-uri/${discoverId} should handle this.
      // For this specific request focusing on AI responses, we assume trackIdxForStems is 0 for discover items if not otherwise specified.
      console.log("[AudioPlayer handleGetStems] Operating on a discoverID, track index defaults to 0 for GCS URI path construction if applicable.");
    }
    // --- End Determine track index ---

    setStemTrackName(title || 'Track');
    setIsGeneratingStems(true);
    setStemProgress(0);
    setToastMessage(null); // Clear previous toasts

    //console.log(`[AudioPlayer] Initiating stem generation for response ID: ${aiResponseId}`);

    try {
      // 1. Fetch GCS URI
      const gcsUriPath = discoverId
        ? `/api/discover-gcs-uri/${discoverId}` // Endpoint for discover items
        : `/api/ai-response-gcs-uri/${aiResponseId}/${trackIdxForStems}`; // Use determined track index for AI responses

      const gcsResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}${gcsUriPath}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}` // Assuming getCookie is available
          }
        }
      );
      const gcsData = await gcsResponse.json();

      if (!gcsData.success || !gcsData.gcsUri) {
        //console.error("[AudioPlayer] GCS URI Extraction Failed:", gcsData);
        setToastMessage(gcsData.message || "Could not find audio file URI.");
        setToastType("error");
        setIsGeneratingStems(false);
        return;
      }
      const gcsUri = gcsData.gcsUri;
      //console.log(`[AudioPlayer] GCS URI: ${gcsUri}`);

      // 2. Call process-audio endpoint
      const requestId = Date.now().toString();
      setToastMessage(`${stemTrackName || 'Track'} - Stems are being generated...`);
      setToastType("info");
      setStemProgress(1);

      const processResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/process-audio`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          },
          body: JSON.stringify({ gcs_uri: gcsUri, request_id: requestId })
        }
      );
      const processData = await processResponse.json();

      if (processResponse.ok && processData.success) {
        //console.log("[AudioPlayer] Stem separation successful:", processData);
        setStemProgress(3);
        setToastMessage("Stems generated! Downloading...");
        setToastType("info");

        // 3. Download stems
        const stemPaths = Object.values(processData.uploaded_files);
        const downloadResponse = await fetch(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/download-stems`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
            },
            body: JSON.stringify({ stems: stemPaths })
          }
        );

        if (downloadResponse.ok) {
          const finalName = title || 'Audio';
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${finalName}_Stems_by_Wubble.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          setToastMessage("Stems downloaded successfully!");
          setToastType("success");
        } else {
          //console.error("[AudioPlayer] Error downloading stems:", downloadResponse);
          setToastMessage("Stems generated but download failed.");
          setToastType("error");
        }
      } else {
        //console.error("[AudioPlayer] Stem separation failed:", processData);
        setToastMessage(processData.message || processData.details || "Stem separation failed.");
        setToastType("error");
      }
    } catch (error) {
      //console.error("[AudioPlayer] Error during stem process:", error);
      Sentry.captureException(error);
      setToastMessage("An error occurred during stem generation.");
      setToastType("error");
    } finally {
      setIsGeneratingStems(false);
    }
  };

  // Determine the volume icon to display
  const getVolumeIcon = () => {
    if ((volume > 0) & (volume <= 0.3)) {
      return <VolumeOff className="w-5 h-5 text-white" />;
    }
    if (volume > 0.3 && volume < 0.8) {
      return <VolumeLow className="w-5 h-5 text-white" />;
    }
    if (volume === 0) {
      return <VolumeXmark className="w-5 h-5 text-white" />;
    }
    return <Volume className="w-5 h-5 text-white" />; // Full volume
  };

  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false);
  };

  if (!audioSrc) {
    return null;
  }

  // Expanded audio player modal view
  const expandedPlayerModal = (
    <div className="fixed inset-0 [background:linear-gradient(180deg,#2C2A2C_0%,#100F10_100%)] p-4 flex flex-col items-center gap-6 z-20 overflow-hidden">
      <button
        id="close-expanded-player-button"
        onClick={() => setIsExpanded(false)}
        className="self-start text-[#878188] hover:text-white p-2 bg-[rgba(255,255,255,0.05)] rounded-full "
      >
        <ChevronDown className="w-4 h-4 text-white" />
      </button>

      <div className="w-full max-w-lg mx-auto">
        <img
          src={localState.audioImgSrc || imageSrc}
          alt="Cover"
          className="w-full object-cover rounded-md mb-6"
        />

        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2">
            <span className="font-normal text-2xl text-white">
              {title}
            </span>
            {displayBadge && badgeTrackIndex !== null && (
              <span className="flex justify-center items-center p-0.5 rounded bg-white/10 text-xs font-medium text-white/90 flex-shrink-0">
                V{badgeTrackIndex + 1}
              </span>
            )}
          </div>
          <p className="w-full text-base font-light text-[#c5c3c6]">
            {description}
          </p>
        </div>

        <div className="w-full flex flex-col items-center mb-6">
          <div className="w-full flex items-center mb-1">
            <Range
              id="audio-progress-bar"
              step={1}
              min={0}
              max={100}
              values={[
                duration && isFinite(duration) && duration > 0 // Check duration here too
                  ? (currentTime / duration) * 100
                  : 0,
              ]}
              onChange={(values) => {
                //console.log('[AudioPlayer] Expanded Player - Range onChange. values:', values, 'current duration:', duration);
                // It's better to use handleSeek here for consistency and its checks
                handleSeek(values); 
              }}
              renderTrack={({ props, children }) => {
                const progressBackground = `linear-gradient(135deg, #FC10F2 0%, #3078E4 ${
                  (currentTime / duration) * 100
                }%, #404040 ${(currentTime / duration) * 100}%)`;

                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: "8px",
                      width: "100%",
                      borderRadius: "100px",
                      background: progressBackground,
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props, isDragged }) => {
                // Track when dragging starts or ends
                if (isDragged !== isPlaybackDragging) {
                  setIsPlaybackDragging(isDragged);
                  if (isDragged) {
                    posthog.capture("Audio-ProgressBar-DragStart", {
                      is_user_logged_in: true,
                      trackName: title,
                      currentPosition: Math.round(currentTime),
                      percentComplete: Math.round((currentTime / duration) * 100),
                      
                    });
                  }
                }
                
                return (
                  <div
                    {...props}
                    style={{
                      ...props.style,
                      height: isDragged || isPlaybackHovered ? "12px" : "4px",
                      width: isDragged || isPlaybackHovered ? "12px" : "4px",
                      borderRadius: "100%",
                      backgroundColor: "#FFF",
                    }}
                  />
                );
              }}
              className="mx-2 grow h-1 bg-white rounded-[100px]"
            />
          </div>
          <div className="flex items-center w-full justify-between pt-2">
            <span className="text-sm font-extralight text-white">
              {formatTime(currentTime)}
            </span>
            <span className="text-sm font-extralight text-white">
              {duration && isFinite(duration) ? formatTime(duration) : "Live"}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-6 mb-6">
          <button
            id="autoplay-button-expanded"
            onClick={handleAutoplayToggle}
            onMouseEnter={() => setActiveTooltip('autoplay-expanded')}
            onMouseLeave={() => setActiveTooltip(null)}
            className="relative w-11 h-11 flex justify-center items-center hover:bg-white/15 rounded-full"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4l12 8-12 8V4z"/>
              <path d="M12 4l12 8-12 8V4z" opacity="0.6"/>
            </svg>
            {isAutoplayEnabled && (
              <div className="absolute bottom-1 w-1.5 h-1.5 bg-white rounded-full"></div>
            )}
            {activeTooltip === 'autoplay-expanded' && (
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                {isAutoplayEnabled ? 'Disable autoplay' : 'Enable autoplay'}
              </div>
            )}
          </button>
          <button
            id="rewind-button"
            onClick={() => {
              const newTime = Math.max(0, currentTime - 10);
              seekAudio(newTime);              
              posthog.capture("Audio-Rewind-10s", {
                is_user_logged_in: true,
                trackName: title,
                currentTimeBeforeRewind: Math.round(currentTime),
                newTime: Math.round(newTime)
              });
            }}
            onMouseEnter={() => setActiveTooltip('rewind-expanded')}
            onMouseLeave={() => setActiveTooltip(null)}
            disabled={isBuffering || !duration || !isFinite(duration)}
            className="w-11 h-11 flex justify-center items-center hover:bg-white/15 disabled:bg-none rounded-full"
          >
            <TbRewindBackward10 className="w-6 h-6 text-white disabled:text-white/40" />
            {activeTooltip === 'rewind-expanded' && !isBuffering && duration && isFinite(duration) && (
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                Rewind 10s
              </div>
            )}
          </button>
          <button
            id="play-pause-button"
            onClick={handlePlayPause}
            className="w-14 h-14 flex justify-center items-center bg-white hover:bg-white/90 rounded-full"
          >
            {isPlaying ? (
              <PauseFill className="w-6 h-6 text-[#100f10]" />
            ) : (
              <PlayFill className="w-6 h-6 text-[#100f10]" />
            )}
          </button>
          <button
            id="fast-forward-button"
            onClick={() => {
              const newTime = duration && isFinite(duration) ? Math.min(duration - 1, currentTime + 10) : currentTime + 10;
              seekAudio(newTime);
              posthog.capture("Audio-FastForward-10s", {
                trackName: title,
                currentTimeBeforeForward: Math.round(currentTime),
                newTime: Math.round(newTime),
                is_user_logged_in: true,
              });
            }}
            onMouseEnter={() => setActiveTooltip('fast-forward-expanded')}
            onMouseLeave={() => setActiveTooltip(null)}
            disabled={isBuffering || !duration || !isFinite(duration)}
            className="w-11 h-11 flex justify-center items-center hover:bg-white/15 disabled:bg-none rounded-full"
          >
            <TbRewindForward10 className="w-6 h-6 text-white disabled:text-white/40" />
            {activeTooltip === 'fast-forward-expanded' && !isBuffering && duration && isFinite(duration) && (
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                Fast forward 10s
              </div>
            )}
          </button>
          <button
            id="repeat-button-expanded"
            onClick={handleRepeatToggle}
            onMouseEnter={() => setActiveTooltip('repeat-expanded')}
            onMouseLeave={() => setActiveTooltip(null)}
            className="relative w-11 h-11 flex justify-center items-center hover:bg-white/15 rounded-full"
          >
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zM17 17H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
            </svg>
            {isRepeatEnabled && (
              <div className="absolute bottom-1 w-1.5 h-1.5 bg-white rounded-full"></div>
            )}
            {activeTooltip === 'repeat-expanded' && (
              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                {isRepeatEnabled ? 'Disable repeat' : 'Enable repeat'}
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col w-full h-full">
      {isExpanded ? (
        expandedPlayerModal
      ) : (
        <div className="relative w-full bg-[#1d1b1d] pt-2 md:pb-1 pb-2 px-4 flex items-center">
          <div className="flex items-center gap-0 grow justify-between content-center">
            <div
              onClick={() => pageWidth < 768 && setIsExpanded(true)}
              className="flex items-center gap-3 grow pr-3 max-w-72"
            >
              <img
                src={localState.audioImgSrc || imageSrc}
                alt="Cover"
                className="w-12 h-12 object-cover rounded-md"
              />
              <div className="flex flex-col justify-center gap-0 grow min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-normal text-base text-white truncate">
                    {title}
                  </span>
                  {displayBadge && badgeTrackIndex !== null && (
                    <span className="flex justify-center items-center p-0.5 rounded bg-white/10 text-xs font-medium text-white/90 flex-shrink-0">
                      V{badgeTrackIndex + 1}
                    </span>
                  )}
                </div>
                <span className="text-xs font-extralight text-[#c5c3c6] truncate w-full">
                  {description}
                </span>
              </div>
            </div>
            <div className="hidden md:flex flex-col items-center gap-0.5 grow max-w-[35rem]">
              <div className="flex items-center gap-2">
                <button
                  id="autoplay-button-compact"
                  onClick={handleAutoplayToggle}
                  onMouseEnter={() => setActiveTooltip('autoplay-compact')}
                  onMouseLeave={() => setActiveTooltip(null)}
                  className="relative w-8 h-8 flex justify-center items-center hover:bg-white/10 rounded-full"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4l12 8-12 8V4z"/>
                    <path d="M12 4l12 8-12 8V4z" opacity="0.6"/>
                  </svg>
                  {isAutoplayEnabled && (
                    <div className="absolute bottom-0 w-1 h-1 bg-white rounded-full"></div>
                  )}
                  {activeTooltip === 'autoplay-compact' && (
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                      {isAutoplayEnabled ? 'Disable autoplay' : 'Enable autoplay'}
                    </div>
                  )}
                </button>
                <button
                  id="rewind-button"
                  onClick={() => {
                    const newTime = Math.max(0, currentTime - 10);
                    seekAudio(newTime);
                    posthog.capture("Audio-Rewind-10s", {
                      is_user_logged_in: true,
                      trackName: title,
                      currentTimeBeforeRewind: Math.round(currentTime),
                      newTime: Math.round(newTime)
                    });
                  }}
                  onMouseEnter={() => setActiveTooltip('rewind-compact')}
                  onMouseLeave={() => setActiveTooltip(null)}
                  disabled={isBuffering || !duration || !isFinite(duration)}
                  className={`w-8 h-8 flex justify-center items-center ${
                    !isBuffering && !(!duration || !isFinite(duration)) && `hover:bg-white/10`
                  } rounded-full disable:opacity/50 disabled:cursor-not-allowed`}
                >
                  <TbRewindBackward10 className={`w-5 h-5 text-white ${
                      (isBuffering || !duration || !isFinite(duration)) && `text-white/40`
                    }`} />
                  {activeTooltip === 'rewind-compact' && !isBuffering && duration && isFinite(duration) && (
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                      Rewind 10s
                    </div>
                  )}
                </button>
                <button
                  id="play-pause-button"
                  onClick={handlePlayPause}
                  className="w-10 h-10 flex justify-center items-center bg-white hover:bg-white/90 rounded-full"
                >
                  {isPlaying ? (
                    <PauseFill className="w-4 h-4 text-[#100f10]" />
                  ) : (
                    <PlayFill className="w-4 h-4 text-[#100f10]" />
                  )}
                </button>
                <button
                  id="fast-forward-button"
                  onClick={() => {
                    const newTime = duration && isFinite(duration) ? Math.min(duration - 1, currentTime + 10) : currentTime + 10;
                    seekAudio(newTime);
                    posthog.capture("Audio-FastForward-10s", {
                      trackName: title,
                      currentTimeBeforeForward: Math.round(currentTime),
                      newTime: Math.round(newTime),
                      is_user_logged_in: true,
                    });
                  }}
                  onMouseEnter={() => setActiveTooltip('fast-forward-compact')}
                  onMouseLeave={() => setActiveTooltip(null)}
                  disabled={isBuffering || !duration || !isFinite(duration)}
                  className="w-11 h-11 flex justify-center items-center hover:bg-white/15 disabled:bg-none rounded-full"
                >
                  <TbRewindForward10 className="w-6 h-6 text-white disabled:text-white/40" />
                  {activeTooltip === 'fast-forward-compact' && !isBuffering && duration && isFinite(duration) && (
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                      Fast forward 10s
                    </div>
                  )}
                </button>
                <button
                  id="repeat-button-compact"
                  onClick={handleRepeatToggle}
                  onMouseEnter={() => setActiveTooltip('repeat-compact')}
                  onMouseLeave={() => setActiveTooltip(null)}
                  className="relative w-8 h-8 flex justify-center items-center hover:bg-white/10 rounded-full"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zM17 17H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
                  </svg>
                  {isRepeatEnabled && (
                    <div className="absolute bottom-0 w-1 h-1 bg-white rounded-full"></div>
                  )}
                  {activeTooltip === 'repeat-compact' && (
                    <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-[#2C2A2C] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
                      {isRepeatEnabled ? 'Disable repeat' : 'Enable repeat'}
                    </div>
                  )}
                </button>
              </div>
              <div className="hidden md:flex items-center gap-2 w-full relative">
                {isCurrentAudioBuffering && (
                  <div className="absolute left-[-24px] top-0 bottom-0 flex items-center justify-center">
                    <div className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-solid border-r-transparent border-[#bc1bb4] align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                  </div>
                )}
                <span className="text-xs font-extralight text-white">
                  {formatTime(currentTime)}
                </span>
                <div
                  className="w-full"
                  onMouseEnter={() => setIsPlaybackHovered(true)}
                  onMouseLeave={() => setIsPlaybackHovered(false)}
                >
                  <Range
                    id="audio-progress-bar"
                    step={1}
                    min={0}
                    max={100}
                    values={[
                      duration && isFinite(duration) && duration > 0 // Check duration here too
                        ? (currentTime / duration) * 100
                        : 0,
                    ]}
                    onChange={(values) => {
                      //console.log('[AudioPlayer] Main Player - Range onChange. values:', values, 'current duration:', duration);
                      handleSeek(values);
                    }}
                    renderTrack={({ props, children }) => {
                      const progressBackground =
                        isPlaybackHovered || isPlaybackDragging
                          ? `linear-gradient(135deg, #FC10F2 0%, #3078E4 ${
                              (currentTime / duration) * 100
                            }%, #404040 ${(currentTime / duration) * 100}%)`
                          : getTrackBackground({
                              values: [
                                duration && isFinite(duration)
                                  ? (currentTime / duration) * 100
                                  : 0,
                              ],
                              colors: ["white", "#404040"], // Default is white for the progress and gray for the remaining part
                              min: 0,
                              max: 100,
                            });

                      return (
                        <div
                          {...props}
                          style={{
                            ...props.style,
                            height: "4px",
                            width: "100%",
                            borderRadius: "100px",
                            background: progressBackground,
                          }}
                        >
                          {children}
                        </div>
                      );
                    }}
                    renderThumb={({ props, isDragged }) => {
                      // Track when dragging starts or ends
                      if (isDragged !== isPlaybackDragging) {
                        setIsPlaybackDragging(isDragged);
                        if (isDragged) {
                          posthog.capture("Audio-ProgressBar-DragStart", {
                            trackName: title,
                            currentPosition: Math.round(currentTime),
                            percentComplete: Math.round((currentTime / duration) * 100),
                            is_user_logged_in: true
                          });
                        }
                      }
                      
                      return (
                        <div
                          {...props}
                          style={{
                            ...props.style,
                            height: isDragged || isPlaybackHovered ? "12px" : "4px",
                            width: isDragged || isPlaybackHovered ? "12px" : "4px",
                            borderRadius: "100%",
                            backgroundColor: "#FFF",
                          }}
                        />
                      );
                    }}
                    className="mx-2 grow h-1 bg-white rounded-[100px]"
                  />
                </div>
                <span className="text-xs font-extralight text-white">
                  {duration && isFinite(duration)
                    ? formatTime(duration)
                    : "Live"}
                </span>
              </div>
            </div>
            <div className="flex justify-end items-center gap-5 grow pl-3 md:max-w-72">
              <div className="hidden md:flex items-center py-3 gap-2">
                <button
                  id="mute-button"
                  onClick={() => mute()}
                  className="w-4 h-4 flex justify-center items-center rounded-full"
                >
                  {getVolumeIcon()}
                </button>
                {showVolumeSlider && (
                  <div
                    onMouseEnter={() => setIsVolumeHovered(true)}
                    onMouseLeave={() => setIsVolumeHovered(false)}
                  >
                    <Range
                      id="volume-slider"
                      step={1}
                      min={0}
                      max={100}
                      values={[volume * 100]}
                      onChange={handleVolumeChange}
                      renderTrack={({ props, children }) => {
                        const volumeBackground =
                          isVolumeHovered || isVolumeDragging
                            ? `linear-gradient(135deg, #FC10F2 0%, #3078E4 ${
                                volume * 100
                              }%, #404040 ${volume * 100}%)`
                            : getTrackBackground({
                                values: [volume * 100],
                                colors: ["white", "#404040"], // Default white progress and gray remainder
                                min: 0,
                                max: 100,
                              });

                        return (
                          <div
                            {...props}
                            style={{
                              ...props.style,
                              height: "5px",
                              width: "4rem",
                              borderRadius: "3px",
                              background: volumeBackground,
                            }}
                          >
                            {children}
                          </div>
                        );
                      }}
                      renderThumb={({ props, isDragged }) => {
                        // Track when volume dragging starts or ends
                        if (isDragged !== isVolumeDragging) {
                          setIsVolumeDragging(isDragged);
                          if (isDragged) {
                            posthog.capture("Audio-VolumeBar-DragStart", {
                              trackName: title,
                              currentVolume: Math.round(volume * 100),
                              is_user_logged_in: true
                            });
                          }
                        }
                        
                        return (
                          <div
                            {...props}
                            style={{
                              ...props.style,
                              height: isDragged || isVolumeHovered ? "12px" : "5px",
                              width: isDragged || isVolumeHovered ? "12px" : "5px",
                              borderRadius: "100%",
                              backgroundColor: "#FFF",
                            }}
                          />
                        );
                      }}
                      className="w-16 mx-2 h-1"
                    />
                  </div>
                )}

              </div>
              <div
                ref={dropdownRef}
                className="flex flex-row md:gap-4 gap-6 relative"
              >
                <button
                  id="play-pause-button"
                  onClick={handlePlayPause}
                  className="md:hidden flex justify-center items-center"
                >
                  {isPlaying ? (
                    <PauseFill className="w-4 h-4 text-[#ffffff]" />
                  ) : (
                    <PlayFill className="w-4 h-4 text-[#ffffff]" />
                  )}
                </button>
                <div className="md:h-8.5 md:w-8.5 md:p-[1.7px] md:hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500 md:rounded-full">
                  <button
                    id="audio-player-dropdown-button"
                    onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
                    className="md:w-7 md:h-7 flex justify-center items-center md:bg-[#2c2a2c] md:rounded-full"
                  >
                    <Ellipsis className="w-4 h-4 md:text-white text-[#878188]" />
                  </button>
                </div>
                {showOptionsDropdown && (
                  <div
                    id="audio-options-dropdown"
                    className="absolute right-0 w-[9rem] text-center text-[#878188] text-sm bottom-9 mt-2 font-normal bg-[#100f10] rounded-lg border border-solid border-[#2c2a2c] shadow-lg z-20"
                  >
                    {aiResponseId && !discoverId && (
                      <div
                        id="audio-download-button"
                        className={`flex items-center transition duration-200 m-1 py-2 px-2 rounded-md ${
                          !isActionEnabled
                            ? 'opacity-50 cursor-not-allowed' // Disabled style
                            : 'hover:bg-zinc-900 hover:text-white cursor-pointer' // Enabled style
                        }`}
                        onClick={isActionEnabled ? handleDownload : undefined} // Prevent click if disabled
                      >
                        <ArrowShapeDownToLine className="mr-2" />
                        Download
                      </div>
                    )}
                    {aiResponseId && !discoverId && (
                    <div
                      id="audio-get-stems-button"
                      className={`flex items-center transition duration-200 m-1 py-2 px-2 rounded-md group ${
                        !isActionEnabled
                          ? 'opacity-50 cursor-not-allowed' // Disabled style
                          : 'hover:bg-zinc-900 hover:text-white cursor-pointer' // Enabled style
                      }`}
                      onClick={isActionEnabled ? handleGetStems : undefined} // Prevent click if disabled
                    >
                      <img
                        src="/updates-modal/feature-update-icon-2.svg"
                        alt="Get stems"
                        className="w-4 h-4 mr-2 opacity-50 group-hover:opacity-100"
                      />
                      Get stems
                    </div>
                    )}
                    {discoverId &&
                      localState.userProfileData?.remaining_credits > 0 && (
                        <div
                          className="flex items-center m-1 py-2 px-2 transition duration-200 cursor-pointer rounded-md hover:bg-zinc-900 hover:text-white"
                          onClick={handleCustomize}
                        >
                          <MusicNote className="mr-2" />
                          Customize
                        </div>
                      )}
                    <div
                      id="audio-stop-button"
                      className="flex items-center m-1 py-2 px-2 transition duration-200 cursor-pointer rounded-md hover:bg-zinc-900 hover:text-white"
                      onClick={stopAudio}
                    >
                      <Xmark className="mr-2" />
                      Close
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isGeneratingStems ? (
            <ProgressInfoToast
              type="info"
              message={`${stemTrackName || 'Track'} - Stems are being generated...`}
              loading={true}
              excludeCloseButton={false}
              className="-translate-x-1/2 w-[90%] md:w-fit md:min-w-[360px]"
              imageSrc={localState.audioImgSrc || imageSrc}
              currentStep={stemProgress}
              totalSteps={3}
              onClose={() => setIsGeneratingStems(false)}
            />
          ) : toastMessage && (
            <Toast
              type={toastType}
              message={toastMessage}
              onClose={() => setToastMessage(null)}
            />
          )}

          {isDownloadModalOpen && (
             <DownloadModal
              title={title}
              description={description}
              duration={typeof duration === 'string' ? duration : formatTime(duration) || "0:00"}
              imageSrc={localState.audioImgSrc || imageSrc}
              ai_response_id={aiResponseId}
              trackIndex={trackIndexForDownload}
              onClose={handleCloseDownloadModal}
              onError={(message) => {
                setToastMessage(message);
                setToastType("error");
                handleCloseDownloadModal();
              }}
            />
          )}

          {isModalOpen && (
            <Modal
              closeModal={handleCloseModal}
              showCloseButton={false}
              easyClose={true}
            >
              <CustomInput
                audioFile={selectedSong.audioFile}
                imageSrc={selectedSong.image}
                mood={description}
                fromDiscoverPage={true}
                className="w-full p-4 md:w-[40rem]"
                requestData={{ createProject: true }}
                chatPage={false}
              />
            </Modal>
          )}
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
